import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DownloadCloud, ExternalLink, Loader2, RefreshCw, Search, ShieldCheck,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const PAGE = 50;
const VIEWS = [
  ["articles", "Articles"],
  ["skipped", "Skipped articles"],
  ["categories", "Categories"],
  ["categories_skipped", "Skipped categories"],
];

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-3 py-2" data-testid={testid}>
    <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
  </div>
);

export default function KBHelpjuicePreview() {
  const [state, setState] = useState(null);
  const [view, setView] = useState("articles");
  const [action, setAction] = useState("");
  const [q, setQ] = usePersistedState("kb-helpjuice.q", "");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [importing, setImporting] = useState(false);
  const poll = useRef(null);
  const importRunning = !!state?.import?.running;

  const load = useCallback(() => {
    api.get("/kb/helpjuice/preview", { params: { view, action, q, skip, limit: PAGE } })
      .then((r) => setState(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [view, action, q, skip]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // While a scan or an import is running, refresh until it finishes.
  useEffect(() => {
    clearInterval(poll.current);
    if (state?.running || importRunning) poll.current = setInterval(load, 3000);
    return () => clearInterval(poll.current);
  }, [state?.running, importRunning, load]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/kb/helpjuice/preview");
      toast.success("Scanning HelpJuice — this takes about half a minute");
      setState((s) => ({ ...(s || {}), running: true, status: "running" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setStarting(false); }
  };

  const runImport = async () => {
    if (!window.confirm("Import from HelpJuice now?\n\nNothing is added on its own: articles we don't have and articles whose words changed at the source both go to Import review for you to include or apply; nothing you hold is overwritten.")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/kb/helpjuice/import");
      if (data.status === "already_running") toast.info("An import is already running");
      else toast.success("Import started — new articles and source changes go to Import review; nothing is added on its own");
      setState((s) => ({ ...(s || {}), import: { ...((s || {}).import || {}), running: true, status: "running" } }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const t = state?.totals || {};
  const rows = state?.rows || [];
  const total = state?.total || 0;
  const imp = state?.import || {};
  const last = imp.last || {};

  return (
    <div data-testid="kb-helpjuice-page">
      <PageHeader compact title="Import HelpJuice" testid="kb-helpjuice-header"
        subtitle="Preview what the live HelpJuice account would offer for review, then import — nothing lands without your say-so">
        <Button variant="outline" size="sm" data-testid="kb-helpjuice-scan"
          disabled={starting || state?.running} onClick={start}>
          {state?.running || starting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <RefreshCw className="mr-1 h-4 w-4" />}
          {state?.running ? "Scanning…" : "Run preview"}
        </Button>
        <Button size="sm" data-testid="kb-helpjuice-run-import"
          disabled={importing || importRunning || state?.configured === false} onClick={runImport}>
          {importing || importRunning ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <DownloadCloud className="mr-1 h-4 w-4" />}
          {importRunning ? "Importing…" : "Run import"}
        </Button>
      </PageHeader>

      <Card className="mb-4 border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900 dark:bg-emerald-950/30" data-testid="kb-helpjuice-notice">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div className="text-xs text-emerald-900 dark:text-emerald-200">
            <p className="font-semibold">Imports are review-gated and hash-gated — nothing is added automatically.</p>
            <p className="mt-1">
              Articles you don&apos;t hold yet are listed for you to include or decline. Articles you already hold are compared by the hash of their visible text:
              unchanged ones are left alone, and ones whose words changed in HelpJuice are parked in{" "}
              <Link to="/settings?tab=kb-import-review" className="font-medium underline" data-testid="kb-helpjuice-review-link">Import review</Link>{" "}
              for you to apply or dismiss. Nothing is ever added or written over on its own.
            </p>
            {(imp.status || imp.running) && (
              <p className="mt-2 text-[11px] text-emerald-800 dark:text-emerald-300" data-testid="kb-helpjuice-import-state">
                {imp.running ? `Import running — started ${imp.started_at ? new Date(imp.started_at).toLocaleString() : ""}${imp.started_by ? ` by ${imp.started_by}` : ""}…`
                  : imp.status === "error" ? `Last import failed: ${imp.error}`
                    : imp.status === "interrupted" ? "The last import was interrupted by a server restart — run it again (nothing is lost)."
                    : `Last import ${imp.finished_at ? new Date(imp.finished_at).toLocaleString() : ""}: ${last.new ?? 0} new (review) · ${last.changed ?? 0} changed (review) · ${(last.unchanged ?? 0) + (last.baseline ?? 0)} unchanged${last.categories_pending ? ` · ${last.categories_pending} categories pending` : ""}`}
              </p>
            )}
          </div>
        </div>
      </Card>

      {state?.configured === false && (
        <Card className="mb-4 border-rose-200 bg-rose-50 p-4 text-xs text-rose-800" data-testid="kb-helpjuice-unconfigured">
          HelpJuice isn&apos;t configured on this server — the account URL and API key are missing.
        </Card>
      )}
      {state?.status === "error" && (
        <Card className="mb-4 border-rose-200 bg-rose-50 p-4 text-xs text-rose-800" data-testid="kb-helpjuice-error">
          The last scan failed: {state.error}
        </Card>
      )}

      {state?.status === "never_run" && !state?.running ? (
        <Card className="border-slate-200 dark:border-slate-800 py-14 text-center" data-testid="kb-helpjuice-never-run">
          <Search className="mx-auto h-8 w-8 text-slate-200" />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No preview has been run yet.</p>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            Hit “Run preview” to read the live HelpJuice account and compare it with this Knowledge Base.
          </p>
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="kb-helpjuice-totals">
            <Stat label="Live in HelpJuice" value={t.articles_live ?? "—"} testid="kb-hj-stat-live" />
            <Stat label="New (to review)" value={t.would_create ?? "—"} testid="kb-hj-stat-create" />
            <Stat label="Changed (to review)" value={t.would_flag ?? "—"} testid="kb-hj-stat-update" />
            <Stat label="Unchanged" value={t.unchanged ?? "—"} testid="kb-hj-stat-unchanged" />
            <Stat label="Skipped" value={t.articles_skipped ?? "—"} testid="kb-hj-stat-skipped" />
            <Stat label="Categories" value={t.categories_scanned ?? "—"} testid="kb-hj-stat-cats" />
            <Stat label="Empty categories" value={t.categories_skipped ?? "—"} testid="kb-hj-stat-cats-skipped" />
          </div>

          <Card className="border-slate-200 dark:border-slate-800 p-4">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <div className="flex rounded-md border border-slate-200 dark:border-slate-800 p-0.5">
                {VIEWS.map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`kb-helpjuice-view-${v}`}
                    onClick={() => { setView(v); setSkip(0); }}
                    className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                      view === v ? "bg-primary/10 text-primary" : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {view === "articles" && (
                <select data-testid="kb-helpjuice-action" value={action}
                  onChange={(e) => { setAction(e.target.value); setSkip(0); }}
                  className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-600 dark:text-slate-300">
                  <option value="">All articles</option>
                  <option value="create">New only</option>
                  <option value="changed">Changed only</option>
                  <option value="unchanged">Unchanged only</option>
                  <option value="deleted_here">Deleted here (never re-imported)</option>
                </select>
              )}
              <div className="relative min-w-[220px] flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input data-testid="kb-helpjuice-search" value={q} placeholder="Search title, slug or category…"
                  onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="pl-8" />
              </div>
              <span className="text-xs text-slate-400 dark:text-slate-500" data-testid="kb-helpjuice-fetched">
                {state?.fetched_at ? `Scanned ${new Date(state.fetched_at).toLocaleString()}` : ""}
              </span>
            </div>

            {loading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-helpjuice-empty">
                {state?.running ? "Scanning HelpJuice…" : "Nothing to show for those filters."}
              </div>
            ) : view === "articles" ? (
              <ArticleTable rows={rows} />
            ) : view === "categories" ? (
              <CategoryTable rows={rows} />
            ) : (
              <SkippedTable rows={rows} />
            )}

            {total > PAGE && (
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>Showing {skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" data-testid="kb-helpjuice-prev"
                    disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>Previous</Button>
                  <Button variant="outline" size="sm" data-testid="kb-helpjuice-next"
                    disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Next</Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

const PILL = {
  create: ["New (to review)", "bg-emerald-100 text-emerald-700"],
  changed: ["Changed", "bg-amber-100 text-amber-800"],
  unchanged: ["Unchanged", "bg-slate-100 text-slate-500"],
  deleted_here: ["Deleted or declined here", "bg-rose-100 text-rose-700"],
  update: ["In KB", "bg-sky-100 text-sky-700"],
};
const ActionPill = ({ action }) => {
  const [label, tone] = PILL[action] || PILL.update;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone}`}>{label}</span>;
};

const ArticleTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
          <th className="py-2 pr-3 font-semibold">Title</th>
          <th className="py-2 pr-3 font-semibold">Action</th>
          <th className="py-2 pr-3 font-semibold">Category</th>
          <th className="py-2 pr-3 font-semibold">Visibility</th>
          <th className="py-2 pr-3 font-semibold">Words</th>
          <th className="py-2 pr-3 font-semibold">Keywords</th>
          <th className="py-2 pr-3 font-semibold">Updated in HelpJuice</th>
          <th className="py-2 font-semibold">Source</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.source_id} data-testid={`kb-helpjuice-row-${r.source_id}`}
            className="border-b border-slate-50 dark:border-slate-800 align-top hover:bg-slate-50/60">
            <td className="py-2.5 pr-3">
              <div className="font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
              <div className="font-mono text-[11px] text-slate-400 dark:text-slate-500">/{r.slug}</div>
              {r.action !== "create" && r.existing_name && r.existing_name !== r.name && (
                <div className="mt-0.5 text-[11px] text-amber-700">Currently titled “{r.existing_name}”</div>
              )}
            </td>
            <td className="py-2.5 pr-3"><ActionPill action={r.action} /></td>
            <td className="py-2.5 pr-3 text-xs text-slate-600 dark:text-slate-300">{r.category_name || "—"}</td>
            <td className="py-2.5 pr-3 text-xs">
              <span className={r.scope === "external" ? "text-emerald-700" : "text-slate-500 dark:text-slate-400"}>
                {r.scope === "external" ? "Customer-facing" : "Internal"}
              </span>
              {!r.published && <div className="text-[11px] text-amber-700">Draft in HelpJuice</div>}
            </td>
            <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">{r.word_count}</td>
            <td className="py-2.5 pr-3">
              <div className="flex max-w-[180px] flex-wrap gap-1">
                {(r.keywords || []).slice(0, 4).map((k) => (
                  <span key={k} className="rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] text-slate-500 dark:text-slate-400">{k}</span>
                ))}
                {!(r.keywords || []).length && <span className="text-xs text-slate-300 dark:text-slate-600">—</span>}
              </div>
            </td>
            <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">
              {r.source_updated_at ? new Date(r.source_updated_at).toLocaleDateString() : "—"}
            </td>
            <td className="py-2.5">
              {r.source_url ? (
                <a href={r.source_url} target="_blank" rel="noreferrer"
                  data-testid={`kb-helpjuice-source-${r.source_id}`}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              ) : <span className="text-xs text-slate-300 dark:text-slate-600">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CategoryTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
          <th className="py-2 pr-3 font-semibold">Category</th>
          <th className="py-2 pr-3 font-semibold">Action</th>
          <th className="py-2 pr-3 font-semibold">Sits under</th>
          <th className="py-2 pr-3 font-semibold">Articles</th>
          <th className="py-2 font-semibold">Visibility</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.source_id} data-testid={`kb-helpjuice-cat-${r.source_id}`}
            className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/60">
            <td className="py-2.5 pr-3">
              <div className="font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
              <div className="font-mono text-[11px] text-slate-400 dark:text-slate-500">/{r.slug}</div>
            </td>
            <td className="py-2.5 pr-3"><ActionPill action={r.action} /></td>
            <td className="py-2.5 pr-3 text-xs text-slate-600 dark:text-slate-300">{r.parent_name || "Top level"}</td>
            <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">{r.article_count}</td>
            <td className="py-2.5 text-xs text-slate-600 dark:text-slate-300">
              {r.scope === "external" ? "Customer-facing" : "Internal"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SkippedTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
          <th className="py-2 pr-3 font-semibold">Name</th>
          <th className="py-2 pr-3 font-semibold">Why it was skipped</th>
          <th className="py-2 font-semibold">Source</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.source_id} data-testid={`kb-helpjuice-skip-${r.source_id}`}
            className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/60">
            <td className="py-2.5 pr-3 font-medium text-slate-700 dark:text-slate-200">{r.name}</td>
            <td className="py-2.5 pr-3 text-xs text-slate-500 dark:text-slate-400">{r.reason}</td>
            <td className="py-2.5">
              {r.source_url ? (
                <a href={r.source_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              ) : <span className="text-xs text-slate-300 dark:text-slate-600">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
